import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, CreditCard, Sparkles } from "lucide-react";

export default function Billing() {
  const { user, org } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState("");
  const isOwner = user?.role === "owner";

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const subscribe = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key, quantity: 1, origin_url: window.location.origin, org_id: user.org_id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErr(err));
      setLoading("");
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Billing & Plans</h1>
        <p className="text-sm text-slate-500">Manage your PropertyInspect360 subscription</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><CreditCard className="h-5 w-5" /></div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current plan</div>
            <div className="font-display text-xl font-bold text-slate-900">
              {org?.plan ? plans.find((p) => p.lookup_key === org.plan)?.name || org.plan : "Trial"}
            </div>
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${org?.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {org?.subscription_status}
        </span>
      </div>

      {!isOwner && <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800">Only the account owner can change the subscription.</p>}

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((p, i) => {
          const current = org?.plan === p.lookup_key;
          return (
            <div key={p.lookup_key} data-testid={`billing-plan-${p.lookup_key}`}
              className={`relative rounded-2xl border p-7 ${i === 1 ? "border-blue-600 shadow-lg" : "border-slate-200"} bg-white`}>
              {i === 1 && <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1 font-mono text-xs uppercase tracking-widest text-white"><Sparkles className="h-3 w-3" />Popular</span>}
              <div className="font-display text-xl font-bold uppercase text-slate-900">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-slate-900">${p.price}</span>
                <span className="mb-1 text-sm text-slate-500">/mo</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /><span className="text-slate-700">{f}</span></li>
                ))}
              </ul>
              <button data-testid={`subscribe-${p.lookup_key}`} disabled={!isOwner || current || loading === p.lookup_key}
                onClick={() => subscribe(p.lookup_key)}
                className={`mt-7 w-full rounded-xl px-4 py-3 text-sm font-semibold disabled:opacity-60 ${current ? "bg-emerald-100 text-emerald-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                {current ? "Current plan" : loading === p.lookup_key ? "Redirecting…" : "Subscribe"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">Test card: 4242 4242 4242 4242 · any future expiry · any CVC. Tax handled automatically at checkout.</p>
    </div>
  );
}
